import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '@core/services/local-storage.service';
import { SimpleStore } from '@core/store/simple-store';
import { TranslocoService } from '@jsverse/transloco';
import { HomeApiService } from '@main/home/services/home-api.service';
import { map, Observable, Subject, switchMap, take } from 'rxjs';
import { AppReferenceData } from '@core/models';
import { tap } from 'rxjs/operators';
import { AccountsApiService } from '@main/account/services/accounts-api.service';
import { CurrencyPair, CurrencyService, GeoLocationResult, getCurrencyForCountry } from '@main/checkout/services/currency.service';

export type AppState = {
    competitionCategories: string[]
    currency: string
    countries: { name: string; code: string }[]
    availableLanguages: Language[]
    selectedLanguage: Language | null
    referenceData: AppReferenceData | null
    preferredLanguage: Language | null
    shouldChangeUsername: boolean
    exchangeRates: CurrencyPair[]
    selectedDisplayCurrency: string
}

export interface Language {
    displayName: string;
    locale: string;
    shortName: string;
}

export const CURRENCY_STORAGE_KEY = 'displayCurrency';

export const initialState: AppState = {
    competitionCategories: [],
    currency: '',
    countries: [],
    availableLanguages: [],
    selectedLanguage: null,
    referenceData: null,
    preferredLanguage: null,
    shouldChangeUsername: false,
    exchangeRates: [],
    selectedDisplayCurrency: ''
};

export const LANGUAGE_STORAGE_KEY = 'lang';

@Injectable({
    providedIn: 'root',
})
export class AppStateService extends SimpleStore<AppState> {
    private document = inject(DOCUMENT);

    private homeApiService = inject(HomeApiService);
    private translocoService = inject(TranslocoService);
    private localStorageService = inject(LocalStorageService);
    private accountService = inject(AccountsApiService);
    private currencyService = inject(CurrencyService);

    constructor () {
        super(initialState);
    }

    init () {
        return this.loadReferenceData().pipe(
            switchMap(() => this.loadLanguage()),
            switchMap(() => this.loadUsernameChange()),
            tap(() => this.loadExchangeRates())
        );
    }

    changeLanguage (selectedLang: Language) {
        this.localStorageService.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(selectedLang));
        this.translocoService
            .load(selectedLang.locale)
            .pipe(take(1))
            .subscribe(() => {
                this.setState({ selectedLanguage: selectedLang });
                this.translocoService.setActiveLang(selectedLang.locale);
                this.document.documentElement.lang = selectedLang.locale;
            });
    }

    private loadReferenceData (): Observable<any> {
        return this.homeApiService.getReferenceData().pipe(
            map((data) => {
                const availableLanguages = data['available-languages'];
                const preferredLanguage = this.getPreferredLanguage(availableLanguages);
                return this.setState({
                    competitionCategories: data['competition-category'],
                    currency: data['product.defaultCurrency'],
                    countries: data['countries'],
                    availableLanguages,
                    referenceData: data,
                    preferredLanguage: preferredLanguage
                });
            }),
        );
    }

    private getPreferredLanguage (availableLanguages: Language[]): Language {
        const stored = this.localStorageService.getItem(LANGUAGE_STORAGE_KEY);
        return stored
            ? availableLanguages.find((lang) => lang.locale === JSON.parse(stored).locale) ||
            availableLanguages[0]
            : availableLanguages[1];
    }

    private loadLanguage () {

        return this.translocoService
            .load(this.getState().preferredLanguage?.locale!!).pipe(map(() => {
                const lang = this.getState().preferredLanguage!!;
                this.translocoService.setActiveLang(lang.locale!!);
                this.setState({ selectedLanguage: lang });
                this.translocoService.setActiveLang(lang.locale!!);
                this.document.documentElement.lang = lang.locale;
            }));
    }

    public loadUsernameChange () {
        return this.accountService.shouldChangeUsername().pipe(tap((shouldChange) => {
            return this.setState({ shouldChangeUsername: shouldChange });
        }));
    }

    private loadExchangeRates () {
        const stored = this.localStorageService.getItem(CURRENCY_STORAGE_KEY);
        if (stored !== null) {
            this.setState({ selectedDisplayCurrency: stored });
        }
        this.currencyService.getExchangeRates().subscribe({
            next: (rates) => {
                this.setState({ exchangeRates: rates });
                if (stored === null) {
                    this.autoDetectCurrency(rates);
                }
            },
            error: () => this.setState({ exchangeRates: [] })
        });
    }

    private autoDetectCurrency (rates: CurrencyPair[]) {
        // Try IP-based geolocation first, fall back to browser locale
        this.currencyService.getGeoLocation().subscribe({
            next: (geo) => this.selectCurrencyForCountry(geo.countryCode, rates),
            error: () => {
                const countryCode = this.detectCountryFromBrowser();
                if (countryCode) {
                    this.selectCurrencyForCountry(countryCode, rates);
                }
            }
        });
    }

    private selectCurrencyForCountry (countryCode: string, rates: CurrencyPair[]) {
        const currency = getCurrencyForCountry(countryCode);
        if (!currency) {
            return;
        }
        // Don't auto-select if the detected currency matches the app's default (e.g. RSD for Serbia)
        if (currency === this.getState().currency) {
            return;
        }
        // Only select if the currency is available in the exchange rates
        if (rates.some(r => r.currency === currency)) {
            this.setDisplayCurrency(currency);
        }
    }

    private detectCountryFromBrowser (): string | null {
        const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
        for (const locale of locales) {
            // Locale format: "en-US", "sr-RS", "de-DE", "fr", etc.
            const parts = locale.split('-');
            if (parts.length >= 2) {
                return parts[parts.length - 1].toUpperCase();
            }
        }
        return null;
    }

    setDisplayCurrency (currency: string) {
        this.localStorageService.setItem(CURRENCY_STORAGE_KEY, currency);
        this.setState({ selectedDisplayCurrency: currency });
    }

    convertAmount (amount: number): { converted: number; currency: string } | null {
        const state = this.getState();
        if (!state.selectedDisplayCurrency || !state.exchangeRates.length) {
            return null;
        }
        const pair = state.exchangeRates.find(r => r.currency === state.selectedDisplayCurrency);
        if (!pair) {
            return null;
        }
        return {
            converted: (amount / pair.exchangeRate) * pair.forAmount,
            currency: pair.currency
        };
    }
}
